import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormApiService } from '@app/core/services/form-api.service';
import { ChargeInformationService, ChargeType, PartyDto, PartyService } from '@app/api/dmc';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { isoToRealDate } from '@app/core/utils/iso-to-real-date';
import { Subscription } from 'rxjs';
import { dateRangeMonthsValidator } from '@shared/validators/date-range.validator';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-chargeinformation-request',
  imports: [
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    ReactiveFormsModule,
    SelectDirective,
    TranslatePipe
  ],
  templateUrl: './chargeinformation-request.component.html',
  styleUrl: './chargeinformation-request.component.css'
})
export class ChargeinformationRequestComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  private readonly subscriptions = new Subscription();

  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public ownerOptions: PartyDto[] = [];
  public ownerCollection: KeyValue[] = [];
  public form: FormGroup = new FormGroup({
    chargeType: new FormControl(null, Validators.required),
    chargeTypeId: new FormControl(null),
    owner: new FormControl<string | null>(null, Validators.required),
    start: new FormControl(null, Validators.required),
    end: new FormControl(null, [Validators.required, dateRangeMonthsValidator('start', 'end', 3)]),
  })

  public chargeTypeOptions: KeyValue[] = enumToKeyValue(ChargeType, { omitKeys: ['NotSpecified'], prefixValue: 'chargeInformation.chargeTypes.' });

  constructor(
    private readonly formApiService: FormApiService,
    private readonly partyService: PartyService,
    private readonly chargeInformationService: ChargeInformationService,
  ) {

  }

  public ngOnInit() {
    this.subscriptions.add(
      this.partyService.partyGetChargeInformationPartiesGet()
        .subscribe((items: PartyDto[]) => {
          this.ownerOptions = items;
          this.ownerCollection = items.map(p => ({ key: String(p.id), value: p.name ?? '' }));
        })
    );

    const date = new Date();
    const endDate = new Date(date);
    endDate.setMonth(date.getMonth() + 1);

    this.form.get('start')?.setValue(isoToRealDate(date));
    this.form.get('end')?.setValue(isoToRealDate(endDate));

    // Revalidate end whenever start changes so the month-range error updates live
    this.subscriptions.add(
      this.form.get('start')!.valueChanges.subscribe(() => {
        this.form.get('end')?.updateValueAndValidity({ emitEvent: false });
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public requestChargeInformations(): void {
    if (this.form.valid) {
      // Handle start date (required)
      this.form.get('start')?.setValue(new Date(this.form.get('start')?.value).toISOString().split('T')[0]);

      // Handle end date (optional) - only convert if it has a value
      const endValue = this.form.get('end')?.value;
      if (endValue) {
        this.form.get('end')?.setValue(new Date(endValue).toISOString().split('T')[0]);
      }

      // Defaults charge type to NotSpecified if not set
      const chargeTypeDefault = this.form.get('chargeType');
      if (chargeTypeDefault && (chargeTypeDefault.value === null || chargeTypeDefault.value === undefined || chargeTypeDefault.value === '')) {
        chargeTypeDefault.setValue(ChargeType.NotSpecified);
      }

      const ownerId = this.form.get('owner')?.value as string | null;
      const owner = ownerId ? this.ownerOptions.find(o => String(o.id) === String(ownerId)) ?? null : null;

      this.formApiService.submit(
        this.form,
        (data) => this.chargeInformationService.chargeInformationRequestChargeInformationsPut({
          ...data,
          owner,
        }),
        {
          successMessage: 'chargeInformation.chargeInformationsRequested',
          closeModal: true,
        }
      );
    } else {
      this.form.markAllAsTouched();
    }
  }
}
